import { useMutation, useQuery } from "@tanstack/react-query";

import { useAxios, useApiResponseParser } from "../";

import type { IOrganization } from "../../interfaces";

export default function useOrganization() {
    const { axiosPrivate } = useAxios();
    const { parseSuccessResponseData } = useApiResponseParser();

    function useList() {
        return useQuery({
            queryKey: ["organizations"],
            queryFn: async () => {
                const response = await axiosPrivate.get("/v1/organization/list");
                return parseSuccessResponseData(response);
            },
        });
    }

    function useDescribe(id: string) {
        return useQuery({
            queryKey: ["organizations", id],
            queryFn: async () => {
                if (!id) return null;
                const response = await axiosPrivate.get(`/v1/organization/describe?Id=${id}`);
                return parseSuccessResponseData(response);
            },
            enabled: !!id,
        });
    }

    function useCreate() {
        return useMutation({
            mutationFn: async (data: Partial<IOrganization> & { Name: string }) => {
                const response = await axiosPrivate.post<IOrganization>("/v1/organization/create", data);
                return parseSuccessResponseData(response);
            },
        });
    }

    function useUpdate() {
        return useMutation({
            mutationFn: async (
                data: Partial<IOrganization> & { Id: string; Name: string; IsActive: boolean },
            ) => {
                const response = await axiosPrivate.put<IOrganization>("/v1/organization/update", data);
                return parseSuccessResponseData(response);
            },
        });
    }

    function useListByUser() {
        return useMutation({
            mutationFn: () =>
                axiosPrivate.get(`/v1/organization/list-by-user`).then(parseSuccessResponseData),
        });
    }

    function useAddUser() {
        return useMutation({
            mutationFn: (parameters: { UserId: string; OrganizationId: string; RoleId?: string }) =>
                axiosPrivate.post(`/v1/organization/add-user`, parameters).then(parseSuccessResponseData),
        });
    }

    return {
        useList,
        useDescribe,
        useCreate,
        useUpdate,
        useListByUser,
        useAddUser,
    };
}
